<template>
    <div>
        <!-- product image mobile -->
        <div class="prod-img-fadecolor" :style="imgBackgroundFadeStyle">
            <div class="prod-img-cont align-items-center mx-auto d-flex d-md-none" :style="imgBackgroundStyle">
                <div class="image-container product-img-mobile mx-auto my-md-3">
                    <nuxt-resource
                        :src="$product.getImage340(product)"
                        :alt="`Card image of ${$product.getName(product)}`"
                    />
                    <UIRibbon :product="product" />
                </div>
            </div>
        </div>
        <UIGridContainer class="product-page">
            <UIGridRow>
                <UIGridCol cols="12">
                    <!-- Main product information -->
                    <div class="product-main mb-3 mt-3 mt-md-0">
                        <h1 v-if="$product.getName(product)" class="product-title my-0 mx-auto">
                            <template v-if="isBundle">
                                {{
                                    translate('cart.bundle.productName', {
                                        productName: $product.getName(product),
                                    })
                                }}
                            </template>
                            <template v-else>{{ $product.getName(product) }}</template>
                        </h1>
                        <h1 v-else class="product-title my-0 mx-auto">{{ brand.name }}</h1>

                        <ThemeSlogan></ThemeSlogan>

                        <div
                            v-if="showProductReviews"
                            class="product-rating-wrapper mt-1 mr-2 align-items-center d-flex"
                        >
                            <UIRatingStars :rating="reviews.rating"></UIRatingStars>

                            <UIIcon name="circle" class="mx-2 circle-icon"></UIIcon>
                            <span style="color: #a1b1b6">{{ reviews.rating }}/5</span>
                        </div>
                    </div>
                </UIGridCol>
            </UIGridRow>
            <UIGridRow>
                <UIGridCol cols="12" md="6" class="pr-md-5">
                    <!-- Product image -->
                    <div class="prod-img-fadecolor" :style="imgBackgroundFadeStyle">
                        <div
                            class="prod-img-cont align-items-center mx-auto d-none d-md-flex"
                            :style="imgBackgroundStyle"
                        >
                            <div class="image-container product-img-desktop mx-auto my-md-3">
                                <nuxt-resource
                                    :src="$product.getImage340(product)"
                                    :alt="`Card image of ${$product.getName(product)}`"
                                />
                                <UIRibbon :product="product" />
                            </div>
                        </div>
                    </div>
                    <ServiceNotification
                        v-if="notifications.length"
                        class="mt-2 mb-2 d-block d-md-none"
                        :notifications="notifications"
                    />

                    <!-- Buy button mobile -->
                    <div class="price-button align-self-baseline d-block d-md-none">
                        <div class="d-flex w-100 align-items-start flex-column">
                            <LoyaltyCoinsPill
                                v-if="product.dundleCoinsRewarded"
                                :coins-earned="product.dundleCoinsRewarded"
                            ></LoyaltyCoinsPill>
                            <div class="price text-right align-self-end">
                                <div v-if="isDiscountPrice" class="product-value d-inline-block">
                                    {{ $formatCurrency($product.getValue(product)) }}
                                </div>
                                <div
                                    :class="isDiscountPrice ? 'discounted' : ''"
                                    class="product-price d-inline-block font-weight-bold"
                                >
                                    {{ $formatCurrency($product.getPrice(product)) }}
                                </div>
                            </div>
                        </div>
                        <UIButton
                            v-if="product.stock !== 0"
                            class="buy-btn w-100"
                            variant="primary"
                            @click="pushToCart(product)"
                        >
                            {{ translate('giftcard.orderNowBtn') }}
                        </UIButton>
                        <UIButton v-else class="buy-btn w-100" disabled variant="primary">
                            {{ translate('product.stock') }}
                        </UIButton>
                        <div
                            v-if="
                                product.stock >= 1 &&
                                !highFraudRiskProducts.includes(product.brand.slug) &&
                                !isPaysafecardInItaly
                            "
                        >
                            <div class="separator mt-2 mb-2">{{ translate('giftcard.or') }}</div>
                            <ProductQuickCheckoutDropdown
                                :methods="getMethods"
                                :product="product"
                            ></ProductQuickCheckoutDropdown>
                        </div>
                    </div>

                    <!-- Product USP's -->
                    <ProductUSPs />
                </UIGridCol>
                <UIGridCol cols="12" md="6" class="pl-md-5">
                    <!-- Alternative product section -->
                    <section class="alternative-products pt-4 pt-sm-0 align-self-start">
                        <ServiceNotification
                            v-if="notifications.length"
                            class="d-none d-md-block"
                            :notifications="notifications"
                        />

                        <h2 class="mt-md-0">1.&nbsp;{{ translate('giftcard.selectValue') }}</h2>
                        <product-alternatives
                            class="alternative-products-container"
                            :current-product="product"
                            :alternatives="alternativeProducts"
                            :current-locale="selectedCountry || selectedCurrency"
                        ></product-alternatives>
                    </section>

                    <!-- County-/ regiondropdown section -->
                    <!-- used a v-show to prevent mismatching serverside nodes -->
                    <section
                        v-show="!hideForMicrosoftProduct"
                        v-if="allowsCrossBorder && !hideDropdown && !isGlobal"
                        class="languages-countries pt-4 pt-md-0 mb-md-0"
                    >
                        <h2>
                            <span>2.&nbsp;</span>
                            {{ translate('giftcard.selectRegion') }}
                        </h2>
                        <span class="can-be-redeemed-text">{{ translate('giftcard.usablecountry') }}</span>
                        <ProductDropdown :brand="brand" class="w-100" @select="selectCountryOrCurrency">
                            <template #dropdown-label>
                                {{ translate('giftcard.selectRegion') }}
                            </template>
                        </ProductDropdown>
                    </section>

                    <!-- Buy button desktop -->
                    <div class="price-button mt-4 align-self-baseline d-none d-md-block">
                        <div class="d-flex w-100 justify-content-end justify-content-md-between align-items-center">
                            <div>
                                <LoyaltyCoinsPill
                                    v-if="product.dundleCoinsRewarded"
                                    :coins-earned="product.dundleCoinsRewarded"
                                ></LoyaltyCoinsPill>
                                <div v-if="isBundle" class="d-flex align-items-center mt-2 mb-2">
                                    <UIIcon :name="['far', 'box']" class="box-icon mr-2"></UIIcon>
                                    <span>{{
                                        translate('cart.bundle.size', {
                                            bundleSize,
                                        })
                                    }}</span>
                                </div>
                            </div>
                            <div class="price text-right">
                                <div v-if="isDiscountPrice" class="product-value d-inline-block">
                                    {{ $formatCurrency($product.getValue(product)) }}
                                </div>
                                <div
                                    :class="isDiscountPrice ? 'discounted' : ''"
                                    class="product-price d-inline-block font-weight-bold"
                                >
                                    {{ $formatCurrency($product.getPrice(product)) }}
                                </div>
                            </div>
                        </div>

                        <UIButton
                            v-if="product.stock !== 0"
                            class="buy-btn w-100"
                            variant="primary"
                            @click="pushToCart(product)"
                        >
                            {{ translate('giftcard.orderNowBtn') }}
                        </UIButton>
                        <UIButton v-else class="buy-btn w-100" disabled variant="primary">
                            {{ translate('product.stock') }}
                        </UIButton>
                        <div
                            v-if="
                                product.stock >= 1 &&
                                !highFraudRiskProducts.includes(product.brand.slug) &&
                                !isPaysafecardInItaly
                            "
                        >
                            <div class="separator mt-2 mb-2">{{ translate('giftcard.or') }}</div>
                            <ProductQuickCheckoutDropdown
                                :methods="getMethods"
                                :product="product"
                            ></ProductQuickCheckoutDropdown>
                        </div>
                    </div>
                </UIGridCol>
            </UIGridRow>
        </UIGridContainer>
        <ProductInfoMenu
            :menu-items="menuItems"
            class="w-100 mt-5 d-none d-md-block"
            @jumpto="jumpTo"
        ></ProductInfoMenu>

        <UIGridContainer>
            <BrandPaysafeDistributor
                v-if="$brand.isPaysafecard(brand)"
                class="d-block d-md-none"
            ></BrandPaysafeDistributor>

            <!-- Product info section -->
            <section>
                <div
                    ref="infoSection"
                    class="info-section d-flex mt-md-4"
                    :class="{ 'paysafecard-info': $brand.isPaysafecard(brand) }"
                >
                    <UIGridRow>
                        <UIGridCol class="product-info" cols="12" md="8">
                            <ContentText
                                ref="productInformation"
                                class="product-description"
                                :content="product.brand.productContent"
                                :params="{
                                    value: $product.getTextValue(product),
                                    topPaymentMethods: getTopPaymentMethods,
                                }"
                            />
                            <ContentText
                                ref="howToRedeemYourCode"
                                :content="product.brand.redeemInstructions"
                                :params="{ product: product }"
                            />
                        </UIGridCol>
                        <UIGridCol cols="12" md="4">
                            <BrandPaysafeDistributor
                                v-if="$brand.isPaysafecard(brand)"
                                class="d-none d-md-block mt-4 pt-3"
                            ></BrandPaysafeDistributor>

                            <FeatureContainer
                                :show-product-reviews="false"
                                :class="{ 'mt-4': !$brand.isPaysafecard(brand) }"
                            ></FeatureContainer>
                            <LoyaltyImage />
                        </UIGridCol>
                    </UIGridRow>
                </div>
            </section>
        </UIGridContainer>
    </div>
</template>

<script>
import {
    UIButton,
    UIGridCol,
    UIGridContainer,
    UIGridRow,
    UIIcon,
    UIRatingStars,
    UIRibbon,
} from '@dundle/ui/components';
import { mapActions, mapGetters } from 'vuex';
import { BrandPaysafeDistributor } from '~/components/brand';
import ContentText from '~/components/content/ContentText';
import FeatureContainer from '~/components/feature/FeatureContainer.vue';
import { LoyaltyImage } from '~/components/loyalty';
import LoyaltyCoinsPill from '~/components/loyalty/LoyaltyCoinsPill';
import {
    ProductAlternatives,
    ProductDropdown,
    ProductInfoMenu,
    ProductQuickCheckoutDropdown,
    ProductUSPs,
} from '~/components/product';
import { ServiceNotification } from '~/components/service';
import { ThemeSlogan } from '~/components/theme';
import BreadcrumbMixin from '~/mixins/BreadcrumbMixin';
import SecurityMixin from '~/mixins/SecurityMixin';
import TranslationMixin from '~/mixins/TranslationMixin';
import { productIsBundle, getBundleSize } from '~/utils/cart/';

export default {
    name: 'ProductPage',

    components: {
        UIIcon,
        UIGridCol,
        UIGridRow,
        UIGridContainer,
        UIButton,
        ProductQuickCheckoutDropdown,
        ProductDropdown,
        ContentText,
        UIRatingStars,
        ProductUSPs,
        ThemeSlogan,
        ProductAlternatives,
        ProductInfoMenu,
        FeatureContainer,
        LoyaltyImage,
        UIRibbon,
        BrandPaysafeDistributor,
        LoyaltyCoinsPill,
        ServiceNotification,
    },
    mixins: [TranslationMixin, BreadcrumbMixin, SecurityMixin],
    middleware: 'cache-tags',
    meta: {
        footer: 'version-1',
    },

    validate({ app, route, params }) {
        if (app.$locale.regio === 'DE' || app.$locale.regio === 'US') {
            if (params.brand === 'bit4coin') {
                return false;
            }
        }

        const regex = /(\d+(?:\.\d+)?)-([a-z]{3})-([a-z]{2})/;

        return regex.test(route.params.product);
    },

    async asyncData({ app, params, store, route, error, redirect }) {
        const brandSlug = params.brand;

        const regex = /(\d+(?:\.\d+)?)-([a-z]{3})-([a-z]{2})/;

        // eslint-disable-next-line no-unused-vars
        const [_, v, currency, country] = route.params.product.match(regex);

        const value = Number(v);

        try {
            const productInfo = await app.$api.productInfo.getBySlug(params.brand);

            // Get the brand
            const brand = await app.$api.brand.getBySlug(brandSlug, country, app.$locale.language, currency);

            if (!brand.isActive) {
                error({
                    statusCode: 404,
                    message: 'Brand is inactive',
                });
                return;
            }

            // Get the product id's for the trustpilot component
            const arrayProductIds = await app.$api.brand.getProductIds(params.brand);
            const joinedProductIds = [brand.slug, ...arrayProductIds].slice(0, 49).join(',');

            let reviews;
            try {
                reviews = await app.$api.trustpilot.getProductReviewSummary(joinedProductIds);
            } catch (error) {
                app.$sentry.captureException(error);
            }

            const products = brand.products;

            const product = products.find(product => product.faceValue === value);

            if (!product) {
                const productValue = products[0].faceValue;
                const reverseRoute = app.$locale.reverseRoute(route);
                return redirect(
                    app.$locale.path({
                        ...reverseRoute,
                        params: {
                            ...reverseRoute.params,
                            product: productValue + '-' + currency + '-' + country,
                        },
                    })
                );
            }

            const notifications = await app.$api.notifications.getNotifications();

            return {
                brand,
                product,
                products,
                productInfo,
                joinedProductIds,
                selectedCurrency: brand.selectedCurrency,
                selectedCountry: brand.selectedRegion,
                reviews: {
                    count: reviews?.numberOfReviews?.total,
                    rating: reviews?.starsAverage,
                },
                methods: store.getters['data/getMethods'],
                notifications,
            };
        } catch (e) {
            error({
                statusCode: 404,
                message: e.message,
            });
        }
    },

    data() {
        return {
            product: null,
            brandName: null,
            slice: 1,
            startOffset: 1,
            highFraudRiskProducts: ['transcash', 'pcs', 'razer-gold'],
        };
    },
    head() {
        // const cheapestProduct = this.getCheapestProduct()

        return {
            title: this.productInfo
                ? this.translateParams(this.productInfo.details.title, {
                      value: this.$product.getTextValue(this.product),
                  })
                : this.$product.getName(this.product) + ' | ' + this.$locale.domain,

            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: this.productInfo
                        ? this.translateParams(this.productInfo.details.description, {
                              value: this.$product.getTextValue(this.product),
                          })
                        : this.$product.getName(this.product) + ' | ' + this.$locale.domain,
                },
                {
                    hid: 'robots',
                    name: 'robots',
                    content: this.shouldIndex ? 'index, follow' : 'noindex, nofollow',
                },
            ],

            link: [
                {
                    hid: 'canonical',
                    rel: 'canonical',
                    href: this.$locale.url({ ...this.$product.getRoute(this.product), deriveDomain: true }),
                },
            ],

            __dangerouslyDisableSanitizers: ['script'],
            script: [
                {
                    innerHTML: JSON.stringify(this.structuredData),
                    type: 'application/ld+json',
                },
            ],
        };
    },

    computed: {
        ...mapGetters({
            occasion: 'occasion/getActiveOccasion',
            getMethods: 'data/getMethods',
            detectedRegion: 'locale/detectedRegion',
            isLocal: 'ip/isLocal',
        }),
        isPaysafecardInItaly() {
            return this.$brand.isPaysafecard(this.brand) && this.$locale.regio === 'IT';
        },
        isNeosurfInNorway() {
            return this.$brand.isNeosurf(this.brand) && this.$locale.regio === 'NO';
        },
        structuredData() {
            const product = this.product;

            const productDescription = this.productInfo
                ? this.translateParams(this.productInfo.details.description, {
                      value: this.$product.getTextValue(this.product),
                  })
                : this.$product.getName(this.product) + ' | ' + this.$locale.domain;

            const productPrice =
                Math.min(
                    this.product.price,
                    this.product.faceValue * this.$currency.exchangeFrom(this.product.currencyCode)
                ) * this.$currency.exchangeTo(this.$locale.currency);

            return {
                '@context': 'http://schema.org/',
                '@type': 'Product',
                name: this.$product.getName(product),
                image: [this.$product.getGoogleShoppingImage(product)],
                description: productDescription,
                gtin13: product.ean13,

                brand: {
                    name:
                        product.brand.brandName && product.brand.brandName.name
                            ? product.brand.brandName.name
                            : product.brand.name,
                },

                offers: {
                    '@type': 'Offer',
                    priceCurrency: this.$locale.currency,
                    price: +productPrice.toFixed(2),
                    itemCondition: 'https://schema.org/NewCondition',
                    availability: 'http://schema.org/' + (product.stock > 0 ? 'InStock' : 'OutOfStock'),
                    seller: {
                        '@type': 'Organization',
                        name: this.$locale.getGlobal('websiteName'),
                    },
                },
            };
        },

        imgBackgroundStyle() {
            return {
                'background-image':
                    'url(https://images.dundle.com/resources/images/products/product-background/' +
                    this.brand.slug +
                    '.jpg) ',
                'background-size': 'cover',
                'background-position': 'center',
            };
        },
        imgBackgroundFadeStyle() {
            return { background: 'linear-gradient(0deg, rgba(255,255,255,1) 0%, ' + this.brand.color + ' 100%)' };
        },
        menuItems() {
            return [
                'productInformation',
                ...(this.product.brand.redeemInstructions?.value ? ['howToRedeemYourCode'] : []),
                // ... this.showProductReviews ? ["trustbox"] : []
            ];
        },

        serviceCost() {
            return this.$product.getServiceCosts(this.product);
        },
        isDiscountPrice() {
            return this.$product.getDiscount(this.product) > 0;
        },

        alternativeProducts() {
            return this.products.filter(product => product !== this.product);
        },

        showProductReviews() {
            return this.reviews.rating >= 4;
        },
        customName() {
            if (this.product.brand.slug === 'amazon') {
                const mapDomain = domain => {
                    const map = {
                        us: 'com',
                        gb: 'co.uk',
                        fr: 'fr',
                        it: 'it',
                        de: 'de',
                        es: 'es',
                    };

                    if (domain in map) {
                        return map[domain];
                    }

                    return domain;
                };

                return 'Amazon.' + mapDomain(this.product.regionCode);
            }
            return this.product.brand.title?.value;
        },

        isMaxQuantity() {
            return this.$cart.items.some(el => el.productId === this.product.productId) && this.product.stock === 1;
        },

        getTopPaymentMethods() {
            return `<ul>${this.methods
                .slice(0, 3)
                .map(method => `<li>${method.description}</li>`)
                .join('')}</ul>`;
        },

        allowsCrossBorder() {
            return this.$brand.allowsCrossBorder(this.brand.slug);
        },

        hideDropdown() {
            return this.$brand.isPaysafecard(this.brand) && ['DE', 'AT'].includes(this.$locale.regio);
        },

        disallowCrossBorder() {
            return this.$security.isEnabled && !this.allowsCrossBorder;
        },

        isGlobal() {
            return (
                (this.brand.selectedRegion &&
                    this.brand.regionCodes.length === 1 &&
                    this.brand.regionCodes[0] === 'ww') ||
                (this.brand.selectedCurrency &&
                    this.brand.currencyCodes.length === 1 &&
                    !this.brand.products[0].currencyLocked)
            );
        },

        shouldIndex() {
            const indexedProducts = [
                'neosurf',
                'zalando',
                'itunes',
                'netflix',
                'playstation',
                'roblox',
                'spotify',
                'playstation-plus',
                'pubg-mobile-uc',
            ];
            const isNLRegion =
                this.$locale.iso === 'nl-NL' &&
                (this.brand.selectedRegion === 'nl' || this.brand.selectedCurrency === 'eur');
            const isITRegion =
                this.$locale.iso === 'it-IT' &&
                (this.brand.selectedRegion === 'it' || this.brand.selectedCurrency === 'eur');

            return indexedProducts.includes(this.brand.slug) && (isNLRegion || isITRegion);
        },

        isBundle() {
            return productIsBundle({ product: this.product });
        },

        bundleSize() {
            return getBundleSize({ product: this.product });
        },
    },

    mounted() {
        const category = this.$category.getName(this.brand.category) + ' > ' + this.$brand.getName(this.brand);
        const settings = {
            content_name: this.$product.getName(this.product),
            content_category: category,
            content_ids: [this.product.productId],
            content_type: 'product',
        };
        this.$gtm?.push({ event: 'view_content', ...settings });

        this.$gtm?.push({ event: `page_view_product`, value: this.brand.slug });
    },

    methods: {
        ...mapActions({
            addProductInCart: 'cart/addItem',
            showModal: 'ui/showModal',
        }),

        jumpTo(refname) {
            let element = this.$refs[refname];
            // when 'ref' is on a component, we need to fetch its $el
            if (element.$el) element = element.$el;
            const topPoz =
                element.getBoundingClientRect().top + (window.scrollY || document.documentElement.scrollTop) - 100;
            window.scrollTo({
                top: topPoz,
                behavior: 'smooth',
            });
        },

        selectCountryOrCurrency(selection) {
            if (this.brand.selectedRegion) {
                this.$router.push(this.$product.getPath({ ...this.product, regionCode: selection }));
            } else if (this.brand.selectedCurrency) {
                this.$router.push(this.$product.getPath({ ...this.product, currencyCode: selection }));
            }
        },

        pushToCart(product) {
            if (!this.isMicrosoftApproved) {
                return this.showModal({ name: 'product-cross-border', data: { currency: this.product.currencyCode } });
            }
            if (!this.isLocal && this.disallowCrossBorder && !this.locatedInCorrectRegion) {
                return this.showModal({ name: 'product-cross-border', data: { region: this.product.regionCode } });
            }
            if (this.isBrandBlockedInCountry) {
                return this.showModal({
                    name: 'info',
                    data: {
                        title: this.translate('notavailableincountry.title'),
                        message: this.translate('notavailableincountry.message'),
                        button: this.translate('button.okconfirm'),
                    },
                });
            }
            if (this.isMaxQuantity || this.isQuantityRestricted) {
                return this.showModal('product-max-items');
            }
            if (
                (this.highFraudRiskProducts.includes(this.brand.slug) ||
                    (this.brand.slug === 'neosurf' && product.regionCode === 'gb')) &&
                !this.$cookies.get('deniedFraudWarning')
            ) {
                this.$gtm?.push({ event: 'event_fraud_warning_modal_triggered' });
                return this.showModal({ name: 'fraud-warning', isMediumModal: true, data: { product } });
            }

            if (this.showMultipleBrandsBlockedModal) {
                return this.showModal({
                    name: 'to-cart',
                    data: {
                        title: this.translate('multipleBrands.title'),
                        message: this.translate('multipleBrands.message'),
                        button: this.translate('button.showCart'),
                    },
                });
            }

            // push call to action button to gtm on the product page
            this.$gtm?.push({ event: 'event_product_page_button', value: this.product.productId });

            this.addProductInCart(product);
            this.$router.push(this.$locale.path('cart'));
        },

        formatCurrency(value) {
            return this.$options.filters.currency(value, undefined, undefined, 0);
        },

        getCheapestProduct() {
            // Loop thourgh all alternative products:
            // If any alternative product is cheaper, return it:
            const cheaperProduct = this.products.filter(
                alternativeProduct => alternativeProduct.faceValue < this.product.faceValue
            );

            // If a cheaper product is found:
            if (cheaperProduct != null) {
                return cheaperProduct;
            }

            // Otherwise, return the selected product:
            return this.product;
        },
    },

    breadcrumb({ app }, data) {
        return [
            {
                name: app.$locale.regio.toLowerCase(),
                link: app.$locale.path('index'),
                url: 'https://' + app.$locale.domain + app.$locale.path('index'),
            },
            {
                name: app.$category.getName(data.brand.category),
                link: app.$category.getPath(data.brand.category),
                url: 'https://' + app.$locale.domain + app.$category.getPath(data.brand.category),
            },
            {
                name: app.$brand.getName(data.brand),
                link: app.$brand.getPath(data.brand),
                url: 'https://' + app.$locale.domain + app.$brand.getPath(data.brand),
            },
            {
                name: app.$product.getTextValue(data.product),
                url: 'https://' + app.$locale.domain + app.$product.getPath(data.product),
            },
        ];
    },

    httpCaching: {
        maxAge: 60 * 30,
    },

    pageCaching: {
        maxAge: 60 * 30,
    },
};
</script>

<style lang="scss" scoped>
@use 'sass:math';
@import '../../assets/styles/imageUtils.scss';

.separator {
    display: flex;
    align-items: center;
    text-align: center;
    color: $color-grey-3;

    &::before,
    &::after {
        content: '';
        flex: 1;
        border-bottom: 1px solid $color-grey-3;
    }

    &::before {
        margin-right: 0.25em;
    }

    &::after {
        margin-left: 0.25em;
    }
}

.breadcrumb {
    margin-bottom: 2rem;

    .separator {
        font-size: 0.75rem;
    }

    @media screen and (min-width: 768px) {
        margin-top: -1rem;
    }
}

.ps5-label {
    background: linear-gradient(0.25turn, #9a269a, #0470ff);
    border-radius: 3px;
    align-self: flex-end;
    text-transform: uppercase;
    font-weight: bold;
    white-space: nowrap;
    font-size: 1rem;
    color: white;
    // font-family: 'roboto';
    margin-bottom: 0.4rem;
    padding: 0.05rem 0.4rem;
}
.product-img-desktop {
    position: relative;
    max-width: 16.5rem;
    img {
        width: 100%;
    }
}
.product-img-mobile {
    max-width: 60%;
    position: relative;
    img {
        width: 100%;
    }
}
@media screen and (min-width: 440px) {
    .product-img-mobile {
        max-width: 16.5rem !important;
        position: relative;
        img {
            width: 100%;
        }
    }
}

.product-value {
    text-decoration: line-through;
    font-size: 1rem;
    font-weight: bold;
}

.product-price {
    font-size: 1.6rem;
    color: $color-price;
}

@media screen and (min-width: 768px) {
    .prod-img-cont {
        min-height: 15rem;
        margin-top: 0 !important;
        box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.2);
    }
}
@media screen and (min-width: 480px) {
    .prod-img-cont {
        margin-top: -2rem;
    }
}

.prod-img-cont {
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(0deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.5) 100%);

        @media screen and (min-width: 768px) {
            background: rgba(255, 255, 255, 0.6);
        }
    }
}

.prod-img-cont {
    min-height: 22rem;
    position: relative;
    img {
        @include calculate-image-border-radius(10%);
        position: relative;
        box-shadow: 0 1px 10px rgba(0, 0, 0, 0.25);
    }
}

.service-fee {
    font-size: 0.85rem;
}

.alternative-products-container {
    min-height: 3rem;
}

.circle-icon {
    margin-left: 0.25rem !important;
    margin-right: 0.25rem !important;
    font-size: 0.4rem;
    color: $color-grey-2;
}
.outofstock-btn {
    border: 1px solid $color-grey-3;
    border-radius: 3px;
    padding: 0.6rem 0;
    font-weight: 500;
    color: $color-grey-4;
    cursor: not-allowed;
}

.buy-btn {
    word-wrap: break-word;
}

#country-dropdown,
#currency-dropdown {
    width: 100%;
}
@media screen and (max-width: 767px) {
    .official-paysafe-distributor {
        border-bottom: 1px solid $color-grey-3;
    }

    .paysafecard-info {
        & > .row {
            background: $color-grey-1;

            .product-info {
                margin-top: 10px;
            }
        }

        .ui-col {
            background: $color-bg;
        }
    }
}
.box-icon {
    height: 1.4rem;
    width: 1.4rem;
    color: $color-primary;
}
</style>
<i18n src="@dundle/locale/data/categories.json"></i18n>
<i18n src="@dundle/locale/data/keywords.json"></i18n>
<i18n src="@dundle/locale/data/countries.json"></i18n>
<i18n src="@dundle/locale/data/giftcard.json"></i18n>
<i18n src="@dundle/locale/data/keywords.json"></i18n>
<i18n src="@dundle/locale/data/cart.json"></i18n>
<i18n src="@dundle/locale/data/warnings.json"></i18n>
<i18n src="@dundle/locale/data/buttons.json"></i18n>
<i18n src="@dundle/locale/data/notavailable.json"></i18n>
